import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Bell, CalendarDays, CheckCheck, FileText, Inbox } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  getUnreadCount,
  listInbox,
  markAllRead,
  markRead,
  type InboxItem,
} from "@/services/notificationsInboxService";

/**
 * Header bell + inbox popover. Mirrors the mobile inbox: lists recent
 * notifications, highlights unread, marks-as-read on tap, navigates to
 * the deep-link target. Resolves the mobile-style `[name]` route
 * templates from the backend (which were designed for expo-router) to
 * portal React Router paths via `resolveInboxDeepLink`.
 */
export function NotificationsBell(): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Cheap badge poll — separate query key from the full list so a list
  // refetch doesn't double-trigger this one and vice versa. Both get
  // invalidated together after mutations.
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // List only loads when the popover opens — keeps the cold load cheap
  // for users who never click the bell.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["notifications", "inbox"],
    queryFn: () => listInbox({ limit: 20 }),
    enabled: open,
    staleTime: 30_000,
  });
  const items = data?.items ?? [];

  const markReadMut = useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllMut = useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const onPressItem = (item: InboxItem) => {
    if (!item.readAt) markReadMut.mutate(item.id);
    const target = resolveInboxDeepLink(item.deepLink);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="fx-top-action relative"
          aria-label={t("header.notifications.label")}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <span
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--fx-accent-600)] px-1 text-[10px] font-semibold leading-none text-white"
              aria-hidden="true"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="end" sideOffset={8}>
        <div className="flex items-center justify-between border-b border-[var(--fx-ink-100)] px-4 py-3 dark:border-[var(--dk-border)]">
          <div>
            <h3 className="text-sm font-semibold tracking-tight text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]">
              {t("header.notifications.title")}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {unreadCount > 0
                ? t("header.notifications.unreadBadge", { count: unreadCount })
                : t("header.notifications.allRead")}
            </p>
          </div>
          {unreadCount > 0 ? (
            <button
              type="button"
              onClick={() => markAllMut.mutate()}
              disabled={markAllMut.isPending}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-[var(--fx-ink-700)] hover:bg-[var(--fx-s-raised-hover)] dark:text-[var(--dk-text-soft)] dark:hover:bg-[var(--fx-s-raised-hover)]"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {t("header.notifications.markAllRead")}
            </button>
          ) : null}
        </div>

        <div className="max-h-[480px] overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("common.loading", "Cargando…")}
            </div>
          ) : isError ? (
            <div className="px-4 py-8 text-center text-sm text-[var(--fx-warn-text)]">
              {t("header.notifications.errorLoading")}
            </div>
          ) : items.length === 0 ? (
            <EmptyInbox />
          ) : (
            <ul className="divide-y divide-[var(--fx-ink-100)] dark:divide-[var(--dk-border)]">
              {items.map((item) => (
                <InboxRow
                  key={item.id}
                  item={item}
                  locale={i18n.language}
                  onPress={() => onPressItem(item)}
                  t={t}
                />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface InboxRowProps {
  item: InboxItem;
  locale: string;
  onPress: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}

function InboxRow({ item, locale, onPress, t }: InboxRowProps): JSX.Element {
  const isUnread = !item.readAt;
  const Icon = useMemo(() => iconFor(item.category), [item.category]);
  return (
    <li>
      <button
        type="button"
        onClick={onPress}
        className={
          "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--fx-s-overlay-hover)] dark:hover:bg-[var(--fx-s-overlay-hover)] " +
          (isUnread ? "bg-[rgba(45,97,73,0.04)] dark:bg-[rgba(111,165,133,0.06)]" : "")
        }
      >
        <span
          className={
            "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg " +
            (isUnread
              ? "bg-[var(--fx-accent-100)] text-[var(--fx-accent-700)] dark:bg-[rgba(111,165,133,0.16)] dark:text-[var(--fx-accent-300)]"
              : "bg-[var(--fx-ink-100)] text-[var(--fx-ink-700)] dark:bg-[rgba(244,239,230,0.06)] dark:text-[var(--dk-text-soft)]")
          }
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p
              className={
                "min-w-0 flex-1 truncate text-sm " +
                (isUnread
                  ? "font-semibold text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]"
                  : "font-medium text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]")
              }
            >
              {item.title}
            </p>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {formatRelativeTime(item.createdAt, locale, t)}
            </span>
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {item.body}
          </p>
        </div>
        {isUnread ? (
          <span
            aria-hidden="true"
            className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[var(--fx-accent-500)]"
          />
        ) : null}
      </button>
    </li>
  );
}

function EmptyInbox(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--fx-ink-100)] dark:bg-[rgba(244,239,230,0.06)]">
        <Inbox className="h-5 w-5 text-[var(--fx-ink-500)]" />
      </span>
      <p className="text-sm font-semibold text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]">
        {t("header.notifications.empty")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("header.notifications.emptyHint")}
      </p>
    </div>
  );
}

function iconFor(category: string) {
  switch (category) {
    case "agenda":
      return CalendarDays;
    case "invoiceReceived":
    case "invoiceStatus":
      return FileText;
    default:
      return Bell;
  }
}

/**
 * Translate the backend's mobile-style route template
 * (`/invoices/[id]` with `params: { id }`) to a portal React Router
 * path. Categories that don't map to a portal page yet (e.g. recibidas
 * routes the mobile app understands but the portal renders elsewhere)
 * fall back to a sensible parent route, or null to skip navigation.
 */
function resolveInboxDeepLink(
  deepLink: { route: string; params?: Record<string, string> } | null,
): string | null {
  if (!deepLink) return null;
  const { route, params } = deepLink;
  // Mobile uses `/invoices/[id]` — substitute the same way push taps do.
  const expanded = route.replace(/\[(\w+)\]/g, (_match, name: string) => {
    const value = params?.[name];
    return typeof value === "string" ? encodeURIComponent(value) : "";
  });
  // Map mobile-shaped routes to portal equivalents. Note: the portal
  // mounts `/invoices/recibidas` and `/invoices/recibidas/:id` directly,
  // so we just forward those — the earlier mapping to "/recibidas" sent
  // users to a non-existent route and rendered a blank screen.
  if (expanded.startsWith("/agenda")) return "/fiscal-agenda";
  if (expanded.startsWith("/invoices/")) return expanded;
  if (expanded.startsWith("/")) return expanded;
  return null;
}

function formatRelativeTime(
  iso: string,
  locale: string,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const now = Date.now();
  const ts = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
  if (diffSec < 60) return t("header.notifications.now");
  if (diffSec < 3600) return t("header.notifications.minutes", { n: Math.floor(diffSec / 60) });
  if (diffSec < 86400) return t("header.notifications.hours", { n: Math.floor(diffSec / 3600) });
  if (diffSec < 86400 * 7)
    return t("header.notifications.days", { n: Math.floor(diffSec / 86400) });
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
  });
}
